import cloneDeep from 'lodash/cloneDeep';

import { initialGlobalFilter, isFilterDate } from '@webapp/account/src/resources/reports/global-filters';
import type { Addons } from '@webapp/account/src/resources/survey/addons/addons';
import { initialAddons } from '@webapp/account/src/resources/survey/addons/addons';
import { initialCss } from '@webapp/account/src/resources/survey/addons/css';
import { initialEmbed } from '@webapp/account/src/resources/survey/addons/embed';
import type { Email } from '@webapp/account/src/resources/survey/addons/notify';
import { initialEmail } from '@webapp/account/src/resources/survey/addons/notify';
import type { Popup } from '@webapp/account/src/resources/survey/addons/popup';
import { initialPopup } from '@webapp/account/src/resources/survey/addons/popup';
import type { Widget } from '@webapp/account/src/resources/survey/addons/widget';
import { initialWidget } from '@webapp/account/src/resources/survey/addons/widget';

import { initialSurveyAddonAlert } from '@webapp/common/resources/entities/survey-addons/alert';
import { initialSurveyAddonBrand } from '@webapp/common/resources/entities/survey-addons/brand';
import { initialSurveyAddonButtons } from '@webapp/common/resources/entities/survey-addons/buttons';
import { initialSurveyAddonFonts } from '@webapp/common/resources/entities/survey-addons/fonts';
import type { SurveyAddonMain } from '@webapp/common/resources/entities/survey-addons/main';
import { initialSurveyAddonMain, normalizeTimerOn } from '@webapp/common/resources/entities/survey-addons/main';
import { initialSurveyAddonParams } from '@webapp/common/resources/entities/survey-addons/params';
import { initialSurveyAddonShare } from '@webapp/common/resources/entities/survey-addons/share';
import { initialSurveyAddonTests } from '@webapp/common/resources/entities/survey-addons/tests';
import { initialSurveyAddonView } from '@webapp/common/resources/entities/survey-addons/view';

import { parseJSON, tuple2Obj } from './utils';

export const mapSurveyFilters = (data) => {
    const filters = typeof data === 'string' ? parseJSON(data) : cloneDeep(data || {});
    // TODO purge, fix data
    filters.linkParamsFilters = Array.isArray(filters.linkParamsFilters) ? filters.linkParamsFilters : [];
    filters.comparePeriods = Array.isArray(filters.comparePeriods) ? filters.comparePeriods : [];

    // Старые/битые addons могли сохранить дату объектом. dayjs на таком
    // значении уходит в рекурсию (FRONT-2ACP/2ACY) — чиним на входе.
    filters.dateFrom = isFilterDate(filters.dateFrom) ? filters.dateFrom : initialGlobalFilter.dateFrom;
    filters.dateTo = isFilterDate(filters.dateTo) ? filters.dateTo : initialGlobalFilter.dateTo;

    filters.excludedParamIds = new Set(Array.isArray(filters.excludedParamIds) ? filters.excludedParamIds : []);
    filters.excludedQuestionIds = new Set(
        Array.isArray(filters.excludedQuestionIds) ? filters.excludedQuestionIds : [],
    );
    filters.excludedCrossTabsId = new Set(
        Array.isArray(filters.excludedCrossTabsId) ? filters.excludedCrossTabsId : [],
    );

    return {
        ...cloneDeep(initialGlobalFilter),
        ...filters,
    };
};

export const deserializeParams = (params: Record<keyof Addons, string>): Addons => {
    const parsedPopupParams = parseJSON<Popup>(params.popup);
    const parsedWidgetParams = parseJSON<Widget>(params.widget);
    const parsedMainParams = params.main ? parseJSON<Partial<SurveyAddonMain>>(params.main) : {};

    return {
        ...cloneDeep(initialAddons),
        alert: {
            ...cloneDeep(initialSurveyAddonAlert),
            ...(params.alert ? parseJSON(params.alert) : {}),
        },
        brand: {
            ...cloneDeep(initialSurveyAddonBrand),
            ...(params.brand ? parseJSON(params.brand) : {}),
        },
        buttons: {
            ...cloneDeep(initialSurveyAddonButtons),
            ...(params.buttons ? parseJSON(params.buttons) : {}),
        },
        embed: {
            ...cloneDeep(initialEmbed),
            ...(params.embed ? parseJSON(params.embed) : {}),
        },
        fonts: {
            ...cloneDeep(initialSurveyAddonFonts),
            ...(params.fonts ? parseJSON(params.fonts) : {}),
        },
        main: {
            ...cloneDeep(initialSurveyAddonMain),
            ...parsedMainParams,
            // timerOn мог сохраниться объектом (наследие старых версий) — приводим к id (FRONT-MAA)
            timerOn: normalizeTimerOn(parsedMainParams.timerOn ?? initialSurveyAddonMain.timerOn),
        },
        popup: {
            ...cloneDeep(initialPopup),
            popup_opened_auto_height: parsedPopupParams.popup_opened_auto_height === true ? true : false,
            ...parsedPopupParams,
        },
        params: {
            ...cloneDeep(initialSurveyAddonParams),
            ...parseJSON(params.params),
        },
        tests: {
            ...cloneDeep(initialSurveyAddonTests),
            ...parseJSON(params.tests),
        },
        view: {
            ...cloneDeep(initialSurveyAddonView),
            ...parseJSON(params.view),
        },
        widget: {
            ...cloneDeep(initialWidget),
            opened_auto_height: parsedWidgetParams.opened_auto_height === true ? true : false,
            ...parsedWidgetParams,
        },
        css: {
            ...cloneDeep(initialCss),
            ...parseJSON(params.css),
        },
        filters: mapSurveyFilters(params.filters),
        notify: {
            emails: (() => {
                const data = parseJSON<{ emails: Array<Email> }>(params.notify, { emails: [] });
                if (!Array.isArray(data.emails)) {
                    data.emails = []; // TODO hotfix
                }

                return data.emails.map((e) => ({
                    ...initialEmail,
                    ...e,
                }));
            })(),
        },
        share: params.share ? parseJSON(params.share) : { ...cloneDeep(initialSurveyAddonShare) },
        mailing: parseJSON(params.mailing, null),
    };
};

export const serializeParams = (params: Addons): Addons =>
    Object.keys(params).reduce((acc, name) => {
        acc[name] = cloneDeep(initialAddons[name]);
        if (acc[name]) {
            Object.keys(acc[name]).forEach((prop) => {
                if (acc[name][prop] instanceof Set) {
                    acc[name][prop] = Array.from(acc[name][prop]);
                }
            });
        }
        return acc;
    }, {}) as Addons;

export const getParamsData = ({
    field,
    value,
    params,
}: {
    field: string | Array<[string, PrimitiveValue]>;
    value?: PrimitiveValue;
    params?: Record<string, any>;
}) => {
    if (params) {
        return params;
    } else if (Array.isArray(field)) {
        return tuple2Obj(field);
    } else {
        return { [field]: value };
    }
};
